import { useEffect } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Creatable, Drawer, Input, useDrawer } from '@iveco-ti/iveco-ds'
import { FormProvider, useForm } from '@iveco-ti/iveco-ds/hook-form'
import { useTranslation } from 'react-i18next'

import { JsonInput } from '@/common/components/JsonInput'
import { useEditApplication } from '../hooks/useEditApplication'
import {
  EditApplicationForm,
  EditApplicationSchema,
} from '../schemas/EditApplicationSchema'
import { useSelectedApplicationStore } from '../stores/useSelectedApplicationStore'

export function DrawerEditApplication() {
  const { t } = useTranslation(['sso', 'common'])
  const { close } = useDrawer('drawer-edit-application')
  const { application } = useSelectedApplicationStore()
  const { handleEditApplication, isEditingApplication } = useEditApplication()

  const hookForm = useForm<EditApplicationForm>({
    resolver: zodResolver(EditApplicationSchema()),
    defaultValues: {
      applicationKey: '',
      title: '',
      description: '',
      tag: [],
      auth: '',
      metadata: '',
    },
  })

  const handleSubmit = async (data: EditApplicationForm) => {
    if (application) {
      await handleEditApplication(application, data).then(() => {
        close()
      })
    }
  }

  const handleCancelEditApplication = () => {
    hookForm.reset()
    close()
  }

  useEffect(() => {
    if (application) {
      hookForm.reset({
        applicationKey: application.applicationKey || '',
        title: application.title || '',
        description: application.description || '',
        tag: application.tag?.map((tag) => ({ label: tag, value: tag })) || [],
        auth: application.auth
          ? JSON.stringify(application.auth, null, 2)
          : '{}',
        metadata: application.metadata
          ? JSON.stringify(application.metadata, null, 2)
          : '{}',
      })
    }
  }, [application, hookForm])

  return (
    <Drawer.Root id="drawer-edit-application">
      <Drawer.Header>
        <h1 className="text-lg text-brand-800 font-semibold">
          {t('sso:applications.drawer_edit_application.header.title')}
        </h1>
      </Drawer.Header>

      <Drawer.Body>
        <h2 className="text-2xl font-semibold text-brand-700">
          {t('sso:applications.drawer_edit_application.body.title')}
        </h2>

        <FormProvider {...hookForm}>
          <form
            className="space-y-6"
            onSubmit={hookForm.handleSubmit(handleSubmit)}
          >
            <div className="flex gap-4">
              <div className="flex-1">
                <Input.Base
                  name="applicationKey"
                  label={t(
                    'sso:applications.drawer_edit_application.fields.application_key.label',
                  )}
                  placeholder={t(
                    'sso:applications.drawer_edit_application.fields.application_key.placeholder',
                  )}
                />
              </div>

              <div className="flex-1">
                <Input.Base
                  name="title"
                  label={t(
                    'sso:applications.drawer_edit_application.fields.title.label',
                  )}
                  placeholder={t(
                    'sso:applications.drawer_edit_application.fields.title.placeholder',
                  )}
                />
              </div>
            </div>

            <Input.Base
              name="description"
              label={t(
                'sso:applications.drawer_edit_application.fields.description.label',
              )}
              placeholder={t(
                'sso:applications.drawer_edit_application.fields.description.placeholder',
              )}
            />

            <Creatable.Sync
              name="tag"
              icon={null}
              isMulti
              label={t(
                'sso:applications.drawer_edit_application.fields.tag.label',
              )}
              placeholder={t(
                'sso:applications.drawer_edit_application.fields.tag.placeholder',
              )}
            />

            <JsonInput
              name="auth"
              label={t(
                'sso:applications.drawer_edit_application.fields.auth.label',
              )}
              placeholder={t(
                'sso:applications.drawer_edit_application.fields.auth.placeholder',
              )}
            />

            <JsonInput
              name="metadata"
              label={t(
                'sso:applications.drawer_edit_application.fields.metadata.label',
              )}
              placeholder={t(
                'sso:applications.drawer_edit_application.fields.metadata.placeholder',
              )}
            />
          </form>
        </FormProvider>
      </Drawer.Body>

      <Drawer.Footer>
        <div className="flex items-center justify-end gap-4">
          <Button.Root
            variant="secondary"
            color="brand"
            onClick={handleCancelEditApplication}
            disabled={isEditingApplication}
          >
            {t(
              'sso:applications.drawer_edit_application.footer.close_button.label',
            )}
          </Button.Root>

          <Button.Root
            variant="primary"
            color="brand"
            onClick={hookForm.handleSubmit(handleSubmit)}
            type="submit"
            isLoading={isEditingApplication}
          >
            {t(
              'sso:applications.drawer_edit_application.footer.submit_button.label',
            )}
          </Button.Root>
        </div>
      </Drawer.Footer>
    </Drawer.Root>
  )
}
